import { apiFetch } from "../../lib/api";
import { useState, useEffect, useCallback } from "react";
import { EmptyState } from "../ui/EmptyState";

// ── Types ──────────────────────────────────────────────────────

type MemoryKind =
  | "decision"
  | "convention"
  | "architecture"
  | "lesson"
  | "context"
  | "reference";

interface MemoryEntry {
  id: string;
  kind: MemoryKind;
  title: string;
  content: string;
  tags: string[];
  created_at: string;
  updated_at: string;
  source: string;
}

const KIND_OPTIONS: { value: MemoryKind; label: string; icon: string }[] = [
  { value: "decision", label: "Decision", icon: "\u2696" },
  { value: "convention", label: "Convention", icon: "\u2699" },
  { value: "architecture", label: "Architecture", icon: "\u25e6" },
  { value: "lesson", label: "Lesson", icon: "\u2139" },
  { value: "context", label: "Context", icon: "\u25cb" },
  { value: "reference", label: "Reference", icon: "\u21b3" },
];

const KIND_LABELS: Record<MemoryKind, string> = {
  decision: "Decision",
  convention: "Convention",
  architecture: "Architecture",
  lesson: "Lesson",
  context: "Context",
  reference: "Reference",
};

// ── Component ──────────────────────────────────────────────────

export function MemoryWorkbench() {
  const [entries, setEntries] = useState<MemoryEntry[]>([]);
  const [selected, setSelected] = useState<MemoryEntry | null>(null);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [filterKind, setFilterKind] = useState<MemoryKind | "">("");

  // Form state
  const [formTitle, setFormTitle] = useState("");
  const [formKind, setFormKind] = useState<MemoryKind>("decision");
  const [formContent, setFormContent] = useState("");
  const [formTags, setFormTags] = useState("");

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (filterKind) params.set("kind", filterKind);
    fetch(`/api/memory?${params}`)
      .then((r) => r.json())
      .then((d) => setEntries(d.entries || []))
      .catch(() => {});
  }, [filterKind]);

  useEffect(() => {
    load();
  }, [load]);

  // Search
  useEffect(() => {
    if (!search.trim()) {
      load();
      return;
    }
    const timeout = setTimeout(() => {
      fetch(`/api/memory/search?q=${encodeURIComponent(search)}`)
        .then((r) => r.json())
        .then((d) => setEntries(d.results || []))
        .catch(() => {});
    }, 200);
    return () => clearTimeout(timeout);
  }, [search, load]);

  const handleAdd = useCallback(() => {
    const tags = formTags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    apiFetch("/api/memory", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        kind: formKind,
        title: formTitle,
        content: formContent,
        tags,
      }),
    })
      .then((r) => r.json())
      .then(() => {
        setShowForm(false);
        setFormTitle("");
        setFormContent("");
        setFormTags("");
        load();
      })
      .catch(() => {});
  }, [formKind, formTitle, formContent, formTags, load]);

  const handleDelete = useCallback(
    (id: string) => {
      fetch(`/api/memory/${id}`, { method: "DELETE" })
        .then(() => {
          if (selected?.id === id) setSelected(null);
          load();
        })
        .catch(() => {});
    },
    [selected, load],
  );

  // Group entries by kind
  const grouped = entries.reduce(
    (acc, entry) => {
      const k = entry.kind;
      if (!acc[k]) acc[k] = [];
      acc[k].push(entry);
      return acc;
    },
    {} as Record<string, MemoryEntry[]>,
  );

  const fmtDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div style={S.root}>
      {/* ── Left panel: list ── */}
      <div style={S.left}>
        <div style={S.leftHeader}>
          <h3 style={S.heading}>Institutional Memory</h3>
          <button style={S.addBtn} onClick={() => setShowForm((v) => !v)}>
            {showForm ? "\u2715 Cancel" : "+ New Entry"}
          </button>
        </div>

        {/* Search + filter */}
        <div style={S.controls}>
          <input
            style={S.searchInput}
            placeholder="Search memories..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            style={S.kindSelect}
            value={filterKind}
            onChange={(e) => setFilterKind(e.target.value as MemoryKind | "")}
          >
            <option value="">All kinds</option>
            {KIND_OPTIONS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </div>

        {/* New entry form */}
        {showForm && (
          <div style={S.form}>
            <input
              style={S.formInput}
              placeholder="Title"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
            />
            <select
              style={S.formSelect}
              value={formKind}
              onChange={(e) => setFormKind(e.target.value as MemoryKind)}
            >
              {KIND_OPTIONS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.icon} {k.label}
                </option>
              ))}
            </select>
            <textarea
              style={S.formTextarea}
              placeholder="Content (what was decided, the convention, the lesson...)"
              value={formContent}
              onChange={(e) => setFormContent(e.target.value)}
              rows={5}
            />
            <input
              style={S.formInput}
              placeholder="Tags (comma-separated)"
              value={formTags}
              onChange={(e) => setFormTags(e.target.value)}
            />
            <button
              style={{
                ...S.submitBtn,
                opacity: formTitle && formContent ? 1 : 0.4,
              }}
              disabled={!formTitle || !formContent}
              onClick={handleAdd}
            >
              Save Entry
            </button>
          </div>
        )}

        {/* Grouped list */}
        <div style={S.listScroll}>
          {Object.keys(grouped).length === 0 && (
            <EmptyState
              icon="M4 4h16v16H4z M4 12h16 M12 4v16"
              title={search ? "No results found" : "No memories yet"}
              subtitle={search ? "Try a different search term." : "Record decisions, conventions, and lessons to build institutional knowledge."}
              action={!search && !showForm ? { label: "+ Add first entry", onClick: () => setShowForm(true) } : undefined}
              compact
            />
          )}
          {Object.entries(grouped).map(([kind, items]) => (
            <div key={kind}>
              <div style={S.groupHeader}>
                {KIND_OPTIONS.find((k) => k.value === kind)?.icon}{" "}
                {KIND_LABELS[kind as MemoryKind] || kind}
                <span style={S.groupCount}>{items.length}</span>
              </div>
              {items.map((entry) => (
                <div
                  key={entry.id}
                  style={{
                    ...S.listItem,
                    ...(selected?.id === entry.id ? S.listItemActive : {}),
                  }}
                  onClick={() => setSelected(entry)}
                >
                  <div style={S.listItemTitle}>{entry.title}</div>
                  <div style={S.listItemMeta}>
                    {entry.tags.length > 0 && (
                      <span style={S.tagList}>
                        {entry.tags.map((t) => (
                          <span key={t} style={S.tag}>
                            {t}
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ── Right panel: detail ── */}
      <div style={S.right}>
        {selected ? (
          <div style={S.detail}>
            <div style={S.detailHeader}>
              <div>
                <span style={S.detailKind}>
                  {KIND_OPTIONS.find((k) => k.value === selected.kind)?.icon}{" "}
                  {KIND_LABELS[selected.kind]}
                </span>
                <h2 style={S.detailTitle}>{selected.title}</h2>
              </div>
              <button
                style={S.deleteBtn}
                onClick={() => handleDelete(selected.id)}
                title="Delete this entry"
              >
                Delete
              </button>
            </div>
            <div style={S.detailMeta}>
              <span>Created: {fmtDate(selected.created_at)}</span>
              <span style={{ marginLeft: "1.5rem" }}>
                Source: {selected.source}
              </span>
            </div>
            {selected.tags.length > 0 && (
              <div style={S.detailTags}>
                {selected.tags.map((t) => (
                  <span key={t} style={S.tag}>
                    {t}
                  </span>
                ))}
              </div>
            )}
            <div style={S.detailDivider} />
            <div style={S.detailContent}>{selected.content}</div>
          </div>
        ) : (
          <EmptyState
            icon="M4 4h16v16H4z M4 12h16 M12 4v16 M8 4v8 M16 4v8"
            title="Select a memory entry"
            subtitle="Institutional memory persists decisions, conventions, and lessons across sessions. Select an entry from the list or create a new one."
            action={!showForm ? { label: "+ New Entry", onClick: () => setShowForm(true) } : undefined}
          />
        )}
      </div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────

const ACCENT = "#4f8fff";
const BG = "#0d0d0d";
const BG_PANEL = "#1a1b26";
const BG_INPUT = "#1e1e2e";
const TEXT = "#f8f8f2";
const TEXT_DIM = "#6b7a8d";
const BORDER = "rgba(255,255,255,0.06)";
const MONO = "'JetBrains Mono', 'Fira Code', 'SF Mono', Consolas, monospace";

const S: Record<string, React.CSSProperties> = {
  root: {
    display: "grid",
    gridTemplateColumns: "380px 1fr",
    height: "100%",
    background: BG,
    color: TEXT,
    fontFamily: MONO,
    fontSize: "0.82rem",
    gap: 0,
  },

  // Left panel
  left: {
    display: "flex",
    flexDirection: "column",
    borderRight: `1px solid ${BORDER}`,
    background: BG_PANEL,
    overflow: "hidden",
  },
  leftHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0.8rem 1rem 0.4rem",
  },
  heading: {
    margin: 0,
    fontSize: "0.9rem",
    fontWeight: 600,
    color: ACCENT,
    letterSpacing: "0.02em",
  },
  addBtn: {
    background: "transparent",
    border: `1px solid ${ACCENT}`,
    color: ACCENT,
    borderRadius: "4px",
    padding: "0.25rem 0.6rem",
    fontSize: "0.72rem",
    fontFamily: MONO,
    cursor: "pointer",
  },

  // Controls
  controls: {
    display: "flex",
    gap: "0.4rem",
    padding: "0.4rem 1rem 0.6rem",
  },
  searchInput: {
    flex: 1,
    background: BG_INPUT,
    border: `1px solid ${BORDER}`,
    borderRadius: "4px",
    color: TEXT,
    padding: "0.35rem 0.6rem",
    fontSize: "0.78rem",
    fontFamily: MONO,
    outline: "none",
  },
  kindSelect: {
    background: BG_INPUT,
    border: `1px solid ${BORDER}`,
    borderRadius: "4px",
    color: TEXT,
    padding: "0.35rem 0.5rem",
    fontSize: "0.72rem",
    fontFamily: MONO,
    outline: "none",
  },

  // Form
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "0.4rem",
    padding: "0.6rem 1rem",
    borderBottom: `1px solid ${BORDER}`,
    background: BG,
  },
  formInput: {
    background: BG_INPUT,
    border: `1px solid ${BORDER}`,
    borderRadius: "4px",
    color: TEXT,
    padding: "0.4rem 0.6rem",
    fontSize: "0.78rem",
    fontFamily: MONO,
    outline: "none",
  },
  formSelect: {
    background: BG_INPUT,
    border: `1px solid ${BORDER}`,
    borderRadius: "4px",
    color: TEXT,
    padding: "0.4rem 0.5rem",
    fontSize: "0.78rem",
    fontFamily: MONO,
    outline: "none",
  },
  formTextarea: {
    background: BG_INPUT,
    border: `1px solid ${BORDER}`,
    borderRadius: "4px",
    color: TEXT,
    padding: "0.4rem 0.6rem",
    fontSize: "0.78rem",
    fontFamily: MONO,
    outline: "none",
    resize: "vertical" as const,
    lineHeight: 1.5,
  },
  submitBtn: {
    background: ACCENT,
    color: BG,
    border: "none",
    borderRadius: "4px",
    padding: "0.4rem 0.8rem",
    fontSize: "0.78rem",
    fontFamily: MONO,
    fontWeight: 600,
    cursor: "pointer",
  },

  // List
  listScroll: {
    flex: 1,
    overflowY: "auto" as const,
    padding: "0 0 1rem",
  },
  empty: {
    padding: "2rem 1rem",
    color: TEXT_DIM,
    textAlign: "center" as const,
    fontSize: "0.78rem",
  },
  groupHeader: {
    padding: "0.5rem 1rem 0.3rem",
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase" as const,
    letterSpacing: "0.08em",
    color: TEXT_DIM,
    borderBottom: `1px solid ${BORDER}`,
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
  },
  groupCount: {
    marginLeft: "auto",
    background: BG_INPUT,
    borderRadius: "8px",
    padding: "0.1rem 0.45rem",
    fontSize: "0.65rem",
    color: TEXT_DIM,
  },
  listItem: {
    padding: "0.5rem 1rem",
    cursor: "pointer",
    borderBottom: `1px solid ${BORDER}`,
    transition: "background 0.1s",
  },
  listItemActive: {
    background: `${ACCENT}18`,
    borderLeft: `3px solid ${ACCENT}`,
  },
  listItemTitle: {
    fontWeight: 500,
    fontSize: "0.8rem",
    marginBottom: "0.15rem",
  },
  listItemMeta: {
    display: "flex",
    alignItems: "center",
    gap: "0.3rem",
    fontSize: "0.68rem",
    color: TEXT_DIM,
  },
  tagList: {
    display: "flex",
    gap: "0.25rem",
    flexWrap: "wrap" as const,
  },
  tag: {
    background: `${ACCENT}1a`,
    color: ACCENT,
    borderRadius: "3px",
    padding: "0.08rem 0.35rem",
    fontSize: "0.65rem",
  },

  // Right panel
  right: {
    display: "flex",
    flexDirection: "column",
    overflow: "auto" as const,
    background: BG,
  },
  placeholder: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: TEXT_DIM,
    gap: "0.6rem",
  },
  placeholderIcon: {
    fontSize: "2.5rem",
    opacity: 0.3,
  },
  placeholderText: {
    fontSize: "0.85rem",
    fontWeight: 500,
  },
  placeholderSub: {
    fontSize: "0.72rem",
    maxWidth: "320px",
    textAlign: "center" as const,
    lineHeight: 1.5,
  },

  // Detail
  detail: {
    padding: "1.5rem 2rem",
  },
  detailHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "0.6rem",
  },
  detailKind: {
    fontSize: "0.7rem",
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
    color: ACCENT,
    fontWeight: 600,
  },
  detailTitle: {
    margin: "0.3rem 0 0",
    fontSize: "1.15rem",
    fontWeight: 600,
    lineHeight: 1.3,
  },
  deleteBtn: {
    background: "transparent",
    border: "1px solid #f44",
    color: "#f66",
    borderRadius: "4px",
    padding: "0.25rem 0.6rem",
    fontSize: "0.72rem",
    fontFamily: MONO,
    cursor: "pointer",
  },
  detailMeta: {
    fontSize: "0.7rem",
    color: TEXT_DIM,
    marginBottom: "0.6rem",
  },
  detailTags: {
    display: "flex",
    gap: "0.3rem",
    flexWrap: "wrap" as const,
    marginBottom: "0.6rem",
  },
  detailDivider: {
    height: "1px",
    background: BORDER,
    margin: "0.8rem 0",
  },
  detailContent: {
    whiteSpace: "pre-wrap" as const,
    lineHeight: 1.65,
    fontSize: "0.82rem",
    color: TEXT,
  },
};
